"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWallet, walletQueryKeys, type WalletData } from '../hooks/useWallet';
import { useOfferActions, type OfferOperation } from '../hooks/useOfferActions';
import { useMarkOffersViewed } from '../hooks/useNewOfferCount';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Tabs } from '../ui/components/Tabs';
import { Table } from '../ui/components/Table';
import { Avatar } from '../ui/components/Avatar';
import { Button } from '../ui/components/Button';
import { Badge } from '../ui/components/Badge';
import { AmbassadorBadge } from '../components/AmbassadorBadge';
import { CAFE_BROKER_ADDRESS, BIDDS_BROKER_ADDRESS, DEALS_BROKER_ADDRESS, OPMARKET_BROKER_ADDRESS } from '../lib/marketplace-constants';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { NFTCard } from '../components/nft/NFTCard';
import { formatPoints } from '../components/nft/PointsBadge';
import { useAuthStore } from '../store/authStore';
import { useSignInModalStore } from '../store/signInModalStore';
import * as SubframeCore from '@subframe/core';
import { followWallet, unfollowWallet } from '../api/walletFollows';
import { blockWallet, unblockWallet } from '../api/walletBlocks';
import { dismissOffer } from '../api/dismissedOffers';
import { useNotificationStore } from '../store/notificationStore';
import { getWalletFlipOpportunity, type WalletFlipOpportunity } from '../api/flips';
import { getPublicWalletOfferControls, type WalletOfferControls } from '../api/settings';
import TradeBuilder from '../components/trades/TradeBuilder';
import { apiClient } from '../api/client';
import { useNFTFilters } from '../hooks/useNFTFilters';
import { NFTFilterBar } from '../components/nft/NFTFilterBar';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { IncomingTransferGroups } from '../components/wallet/IncomingTransferGroups';

type TabType = 'nfts' | 'offers' | 'history';
type EventFilter = 'all' | 'buys-sells' | 'transfers';

export function WalletPage() {
  const { id, collectionId } = useParams<{ id: string; collectionId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useWallet(id);
  const { isAuthenticated, user: authUser } = useAuthStore();
  const openSignIn = useSignInModalStore((st) => st.open);

  // Notification store for error/success feedback - must be called before any early returns
  const { error: showError, success: showSuccess } = useNotificationStore();

  // Trade builder state
  const [showTradeBuilder, setShowTradeBuilder] = useState(false);

  /**
   * Optimistically remove an offer from local state when transaction succeeds.
   * This provides immediate UI feedback before the XRPL transaction propagates
   * and the cache refetch completes.
   */
  const handleOfferRemoved = React.useCallback((offerId: string, operation: OfferOperation) => {
    if (!id) return;

    const queryKey = walletQueryKeys.wallet(id);
    queryClient.setQueryData<WalletData>(queryKey, (oldData) => {
      if (!oldData) return oldData;

      // Create new offers object with the removed offer filtered out
      const newOffers = { ...oldData.offers };

      switch (operation) {
        case 'acceptTransfer':
        case 'rejectTransfer':
          newOffers.incomingTransfers = newOffers.incomingTransfers.filter(
            (offer) => offer.offerId !== offerId
          );
          break;
        case 'cancel':
          // Could be from outgoingTransfers, offersMade, or listedNFTs
          newOffers.outgoingTransfers = newOffers.outgoingTransfers.filter(
            (offer) => offer.offerId !== offerId
          );
          newOffers.offersMade = newOffers.offersMade.filter(
            (offer) => offer.offerId !== offerId
          );
          newOffers.listedNFTs = newOffers.listedNFTs.filter(
            (offer) => offer.offerId !== offerId
          );
          break;
        case 'acceptBuyOffer':
          newOffers.offersReceived = newOffers.offersReceived.filter(
            (offer) => offer.offerId !== offerId
          );
          break;
        // acceptSellOffer doesn't remove from this wallet's offers
      }

      return {
        ...oldData,
        offers: newOffers,
      };
    });
  }, [id, queryClient]);

  // Shared offer actions hook
  const {
    processing,
    showXamanModal,
    xamanPayload,
    currentOfferId,
    cancelOffer,
    acceptTransfer,
    rejectTransfer,
    acceptBuyOffer,
    handleTransactionSuccess,
    handleTransactionRejected,
    getModalTitle,
  } = useOfferActions({
    invalidateKeys: [['wallet', id || ''], ['notifications']],
    onOfferRemoved: handleOfferRemoved,
  });

  // Dismiss offer handler
  const [dismissingOfferId, setDismissingOfferId] = useState<string | null>(null);
  const handleDismissOffer = useCallback(async (offerId: string) => {
    setDismissingOfferId(offerId);
    try {
      await dismissOffer(offerId);
      queryClient.invalidateQueries({ queryKey: ['wallet', id || ''] });
      showSuccess('Offer dismissed');
    } catch (err) {
      showError('Failed to dismiss offer');
    } finally {
      setDismissingOfferId(null);
    }
  }, [id, queryClient, showSuccess, showError]);

  // Block address from offer handler
  const [blockingAddress, setBlockingAddress] = useState<string | null>(null);
  const handleBlockFromOffer = useCallback(async (address: string) => {
    if (!window.confirm(`Block ${address}?\n\nAll offers and transfers from this address will be hidden.`)) return;
    setBlockingAddress(address);
    try {
      await blockWallet(address);
      queryClient.invalidateQueries({ queryKey: ['wallet', id || ''] });
      showSuccess('All offers from this wallet are now hidden', { title: 'Wallet Blocked' });
    } catch (err: any) {
      showError(err?.message || 'Failed to block wallet');
    } finally {
      setBlockingAddress(null);
    }
  }, [id, queryClient, showSuccess, showError]);

  // Initialize active tab from URL (default to 'history')
  const initActiveTab = (() => {
    const tab = searchParams.get('tab') as TabType;
    return tab === 'nfts' || tab === 'offers' ? tab : 'history';
  })();

  const [activeTab, setActiveTab] = useState<TabType>(initActiveTab);
  const [eventFilter, setEventFilter] = useState<EventFilter>('buys-sells');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [showUnfundedOffers, setShowUnfundedOffers] = useState(false);

  // Wallet follow state
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [followerCount, setFollowerCount] = useState<number>(0);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const lastFollowClickRef = React.useRef<number>(0);
  const FOLLOW_DEBOUNCE_MS = 500; // Prevent rapid clicking

  // Wallet block state
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isBlockLoading, setIsBlockLoading] = useState(false);
  const lastBlockClickRef = React.useRef<number>(0);
  const BLOCK_DEBOUNCE_MS = 500;

  // Initialize follow and block state from API data
  React.useEffect(() => {
    if (data?.wallet) {
      setIsFollowing(data.wallet.isFollowing || false);
      setFollowerCount(data.wallet.followerCount || 0);
      setIsBlocked(data.wallet.isBlocked || false);
    }
  }, [data?.wallet]);

  // Wallet offer controls state
  const [walletOfferControls, setWalletOfferControls] = useState<WalletOfferControls | null>(null);

  // Fetch wallet offer controls when wallet data loads
  React.useEffect(() => {
    if (data?.wallet?.address) {
      getPublicWalletOfferControls(data.wallet.address)
        .then(setWalletOfferControls)
        .catch(() => setWalletOfferControls(null));
    }
  }, [data?.wallet?.address]);

  // Check if viewing own wallet
  const isOwnWallet = authUser?.xrpl_address === data?.wallet?.address;

  // Mark offers as viewed when user is on their own wallet's offers tab
  const markOffersViewed = useMarkOffersViewed();
  useEffect(() => {
    if (isOwnWallet && activeTab === 'offers') {
      markOffersViewed.mutate();
    }
  }, [isOwnWallet, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flip opportunity state for collection view
  const [flipOpportunity, setFlipOpportunity] = useState<WalletFlipOpportunity | null>(null);

  // Fetch flip opportunity when viewing a specific collection
  React.useEffect(() => {
    if (id && collectionId) {
      getWalletFlipOpportunity(id, collectionId)
        .then((response) => {
          if (response.success && response.data) {
            setFlipOpportunity(response.data);
          } else {
            setFlipOpportunity(null);
          }
        })
        .catch(() => {
          setFlipOpportunity(null);
        });
    } else {
      setFlipOpportunity(null);
    }
  }, [id, collectionId]);

  // If collectionId is provided, filter to show only that collection's NFTs
  const isCollectionView = !!collectionId;
  const selectedCollection = isCollectionView
    ? data?.nfts.collections.find(c => c.collectionId === collectionId)
    : null;

  // Whether this collection is indexed (has a slug for API queries)
  const isIndexedCollection = !!selectedCollection?.collectionSlug;

  // Collection metadata state (trait_stats, has_points_system - for indexed collections)
  const [collectionMeta, setCollectionMeta] = useState<any>(null);
  const [collectionNFTs, setCollectionNFTs] = useState<any[]>([]);
  const [collectionNFTsLoading, setCollectionNFTsLoading] = useState(false);
  const [collectionTotalPages, setCollectionTotalPages] = useState(1);
  const [collectionCurrentPage, setCollectionCurrentPage] = useState(1);

  // filterVersion increments on each Apply/Clear to trigger the load useEffect
  // after React commits the new state (avoids stale closure reads from buildApiParams)
  const [filterVersion, setFilterVersion] = useState(0);

  // NFT filter hook (always called but only active for indexed collections)
  const filters = useNFTFilters({
    defaultSort: 'rarity-low-high',
    syncURL: false,
    onApply: () => {
      setCollectionCurrentPage(1);
      setFilterVersion(v => v + 1);
    },
  });

  // Infinite scroll for collection NFTs
  const collectionSentinelRef = useInfiniteScroll({
    onLoadMore: () => {
      const nextPage = collectionCurrentPage + 1;
      setCollectionCurrentPage(nextPage);
    },
    enabled: isIndexedCollection && !collectionNFTsLoading && collectionCurrentPage < collectionTotalPages,
  });

  // Fetch collection metadata and reset state when entering a new collection view
  useEffect(() => {
    let cancelled = false;
    if (isIndexedCollection && selectedCollection?.collectionSlug) {
      apiClient.get<any>(`/collections/${selectedCollection.collectionSlug}`)
        .then(res => {
          if (!cancelled && res.success && res.data) {
            setCollectionMeta(res.data);
          }
        })
        .catch(() => { if (!cancelled) setCollectionMeta(null); });
    } else {
      setCollectionMeta(null);
    }
    // Reset NFT state and filters when collection changes
    setCollectionNFTs([]);
    setCollectionCurrentPage(1);
    setCollectionTotalPages(1);
    filters.resetFilters();
    return () => { cancelled = true; };
  }, [selectedCollection?.collectionSlug, isIndexedCollection]);

  // Load collection NFTs when page, sort, trait filters, or filterVersion change.
  // Uses refs to detect sort/trait/Apply changes that should reset to page 1.
  // Cancelled flag prevents stale responses from racing (e.g., rapid collection navigation).
  const prevSortRef = React.useRef(filters.sortOption);
  const prevTraitsRef = React.useRef(filters.selectedTraits);
  const prevFilterVersionRef = React.useRef(filterVersion);
  useEffect(() => {
    if (!isIndexedCollection || !selectedCollection?.collectionSlug || !id) return;

    // Determine if sort/trait/Apply changed (reset to page 1) vs page increment
    const sortChanged = prevSortRef.current !== filters.sortOption;
    const traitsChanged = prevTraitsRef.current !== filters.selectedTraits;
    const filterVersionChanged = prevFilterVersionRef.current !== filterVersion;
    prevSortRef.current = filters.sortOption;
    prevTraitsRef.current = filters.selectedTraits;
    prevFilterVersionRef.current = filterVersion;

    const shouldResetPage = sortChanged || traitsChanged || filterVersionChanged;
    const page = shouldResetPage ? 1 : collectionCurrentPage;

    if (shouldResetPage && collectionCurrentPage !== 1) {
      setCollectionCurrentPage(1);
    }
    if (shouldResetPage) {
      setCollectionNFTs([]);
    }

    let cancelled = false;
    const loadPage = async () => {
      setCollectionNFTsLoading(true);
      try {
        const params = filters.buildApiParams();
        params.set('page', page.toString());
        params.set('limit', '20');
        params.set('owner', id);

        const response = await apiClient.get<any>(
          `/collections/${selectedCollection.collectionSlug}/nfts?${params}`
        );

        if (!cancelled && response.success && response.data) {
          if (page === 1) {
            setCollectionNFTs(response.data.nfts || []);
          } else {
            setCollectionNFTs(prev => [...prev, ...(response.data.nfts || [])]);
          }
          if (response.data.pagination) {
            setCollectionTotalPages(response.data.pagination.pages || 1);
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Error loading collection NFTs:', err);
      } finally {
        if (!cancelled) setCollectionNFTsLoading(false);
      }
    };

    loadPage();
    return () => { cancelled = true; };
  }, [collectionCurrentPage, filters.sortOption, filters.selectedTraits, isIndexedCollection, selectedCollection?.collectionSlug, filterVersion, id]);

  // Convert collection metadata trait_stats to the format NFTFilterBar expects
  const getCollectionTraitStats = (): { traitType: string; values: { value: string; count: number; percentage: number }[] }[] => {
    if (!collectionMeta?.trait_stats) return [];

    return Object.entries(collectionMeta.trait_stats).map(([traitType, values]: [string, any]) => ({
      traitType,
      values: Object.entries(values)
        .map(([value, stats]: [string, any]) => ({
          value,
          count: stats.count,
          percentage: stats.percentage,
        }))
        .sort((a, b) => a.percentage - b.percentage),
    }));
  };

  // Loading state
  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading wallet data...</p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Error state
  if (error || !data) {
    // Clean up error message for display
    const errorMessage = error instanceof Error ? error.message : 'Failed to load wallet data';
    const displayMessage = errorMessage.replace(/external\s*/gi, '');

    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-error-600 mb-2">Error</h1>
            <p className="text-gray-600 mb-4">
              {displayMessage}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  const totalOffers =
    data.offers.incomingTransfers.length +
    data.offers.outgoingTransfers.length +
    data.offers.offersReceived.length +
    data.offers.offersMade.length +
    data.offers.listedNFTs.length;

  const formatAddress = (address: string | null): string => {
    if (!address) return 'N/A';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const renderLinkedAddress = (address: string | null) => {
    if (!address) return <span className="text-body font-body text-default-font">N/A</span>;
    return (
      <a
        href={`/wallets/${address}`}
        className="text-body font-body text-brand-700 hover:text-brand-800 hover:underline"
        title={address}
      >
        {formatAddress(address)}
      </a>
    );
  };

  const getOfferSource = (destination: string | null): { label: string; variant: 'brand' | 'neutral' | 'error' | 'warning' | 'success'; className?: string } => {
    if (!destination) return { label: 'Direct', variant: 'success' };
    if (destination === DEALS_BROKER_ADDRESS) return { label: 'xrp.deals', variant: 'brand' };
    if (destination === CAFE_BROKER_ADDRESS) return { label: 'xrp.cafe', variant: 'neutral', className: 'opacity-60' };
    if (destination === BIDDS_BROKER_ADDRESS) return { label: 'bidds.com', variant: 'neutral', className: 'opacity-60' };
    if (destination === OPMARKET_BROKER_ADDRESS) return { label: 'opmarket.ai', variant: 'neutral', className: 'opacity-60' };
    return { label: 'Other', variant: 'neutral', className: 'opacity-60' };
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatExpiration = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    // Already expired
    if (diffMs <= 0) return 'Expired';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return '< 1 hour';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTransactionIcon = (
    type: string,
    price: number | null,
    fromAddress: string | null,
    toAddress: string | null,
    walletAddress: string
  ) => {
    // Determine event type based on transaction details
    if (type === 'NFTokenMint') {
      return <SubframeCore.FeatherPlus className="text-body font-body text-neutral-700" />;
    } else if (type === 'NFTokenBurn') {
      return <SubframeCore.FeatherTrash2 className="text-body font-body text-neutral-700" />;
    } else if (type === 'NFTokenAcceptOffer') {
      if (toAddress === walletAddress) {
        // Receiving - Buy or Transfer In
        return price === null
          ? <SubframeCore.FeatherArrowDownCircle className="text-body font-body text-neutral-700" />
          : <SubframeCore.FeatherShoppingCart className="text-body font-body text-green-600" />;
      } else if (fromAddress === walletAddress) {
        // Sending - Sell or Transfer Out
        return price === null
          ? <SubframeCore.FeatherArrowUpCircle className="text-body font-body text-neutral-700" />
          : <SubframeCore.FeatherDollarSign className="text-body font-body text-red-600" />;
      }
    }
    return <SubframeCore.FeatherCircle className="text-body font-body text-default-font" />;
  };

  const getTransactionLabel = (
    type: string,
    price: number | null,
    fromAddress: string | null,
    toAddress: string | null,
    walletAddress: string
  ): string => {
    switch (type) {
      case 'NFTokenMint':
        return 'Mint';
      case 'NFTokenAcceptOffer':
        // Determine direction based on wallet address
        if (toAddress === walletAddress) {
          // Wallet received the NFT
          return price === null ? 'Transfer In' : 'Buy';
        } else if (fromAddress === walletAddress) {
          // Wallet sent the NFT
          return price === null ? 'Transfer Out' : 'Sell';
        }
        return 'Sale'; // Fallback
      case 'NFTokenBurn':
        return 'Burn';
      case 'NFTokenCreateOffer':
        return 'List';
      case 'NFTokenCancelOffer':
        return 'Cancel';
      default:
        return type;
    }
  };

  const getTransactionLabelColor = (
    type: string,
    price: number | null,
    fromAddress: string | null,
    toAddress: string | null,
    walletAddress: string
  ): string => {
    if (type === 'NFTokenAcceptOffer') {
      if (toAddress === walletAddress && price !== null) {
        // Buy - Green
        return 'text-green-600';
      } else if (fromAddress === walletAddress && price !== null) {
        // Sell - Red
        return 'text-red-600';
      }
    }
    // Transfers, Mint, Burn - White/Default
    return 'text-neutral-700';
  };

  const filterTransactions = (transactions: any[], filter: EventFilter, walletAddress: string) => {
    if (filter === 'all') {
      return transactions;
    }

    return transactions.filter((tx) => {
      const isBuyOrSell = tx.type === 'NFTokenAcceptOffer' && tx.price !== null;
      const isTransfer = tx.type === 'NFTokenAcceptOffer' && tx.price === null;

      if (filter === 'buys-sells') {
        return isBuyOrSell;
      } else if (filter === 'transfers') {
        return isTransfer || tx.type === 'NFTokenMint' || tx.type === 'NFTokenBurn';
      }

      return true;
    });
  };

  const formatHoldTime = (seconds: number | null | undefined): string | null => {
    if (seconds === null || seconds === undefined) return null;

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}yr`;
    if (months > 0) return `${months}mo`;
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    return '<1h';
  };

  const formatProfitLoss = (profitLossXrp: number | null | undefined, roiPercentage: number | null | undefined) => {
    if (profitLossXrp === null || profitLossXrp === undefined) {
      return { display: '–', color: 'text-subtext-color' };
    }

    // Convert to number in case it's a string (PostgreSQL NUMERIC serialization)
    const profitLossNum = typeof profitLossXrp === 'string' ? parseFloat(profitLossXrp) : profitLossXrp;
    const roiNum = roiPercentage !== null && roiPercentage !== undefined
      ? (typeof roiPercentage === 'string' ? parseFloat(roiPercentage) : roiPercentage)
      : null;

    const isProfit = profitLossNum > 0;
    const isLoss = profitLossNum < 0;
    const sign = isProfit ? '+' : isLoss ? '-' : '';
    const color = isProfit ? 'text-success-600' : isLoss ? 'text-error-600' : 'text-subtext-color';

    const formattedValue = Math.abs(profitLossNum).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const roiSign = roiNum !== null && roiNum !== undefined
      ? (roiNum > 0 ? '+' : roiNum < 0 ? '-' : '')
      : '';

    return {
      amount: `${sign}${formattedValue} XRP`,
      percentage: roiNum !== null ? `${roiSign}${Math.abs(roiNum).toFixed(1)}%` : '',
      color
    };
  };

  const formatSellerMetrics = (
    holdTimeSeconds: number | null | undefined,
    profitLossXrp: number | string | null | undefined,
    roiPercentage: number | string | null | undefined
  ): { holdText: string; plText: string; plColor: string } | null => {
    const holdTime = formatHoldTime(holdTimeSeconds);

    // No hold time data at all
    if (!holdTime) {
      return null;
    }

    // Have hold time but no profit/loss data
    if (profitLossXrp === null || profitLossXrp === undefined) {
      return {
        holdText: `Held ${holdTime},`,
        plText: 'P/L unknown',
        plColor: 'text-subtext-color'
      };
    }

    // Convert to number in case it's a string (PostgreSQL NUMERIC serialization)
    const profitLossNum = typeof profitLossXrp === 'string' ? parseFloat(profitLossXrp) : profitLossXrp;
    const roiNum = roiPercentage !== null && roiPercentage !== undefined
      ? (typeof roiPercentage === 'string' ? parseFloat(roiPercentage) : roiPercentage)
      : null;

    // Have both hold time and profit/loss
    const isProfit = profitLossNum > 0;
    const isLoss = profitLossNum < 0;
    const sign = isProfit ? '+' : isLoss ? '-' : '';
    const plColor = isProfit ? 'text-success-600' : isLoss ? 'text-error-600' : 'text-subtext-color';
    const formattedValue = Math.abs(profitLossNum).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const verb = isProfit ? 'profited' : isLoss ? 'lost' : '';

    // Format ROI percentage if available
    let roiText = '';
    if (roiNum !== null) {
      const roiSign = roiNum > 0 ? '+' : roiNum < 0 ? '-' : '';
      roiText = ` (${roiSign}${Math.abs(roiNum).toFixed(1)}%)`;
    }

    return {
      holdText: `Held ${holdTime},`,
      plText: `${verb} ${sign}${formattedValue} XRP${roiText}`,
      plColor
    };
  };

  // Get P&L metrics from database (calculated by wallet metrics cron job)
  // Falls back to null if wallet metrics not yet available
  const pnlSummary = {
    // Realized P&L (from NFTs bought and sold)
    realizedPnL: data?.wallet.total_profit ?? null,
    realizedROI: data?.wallet.roi ?? null,
    // Unrealized P&L (from NFTs still held, valued at floor price)
    unrealizedPnL: data?.wallet.unrealized_profit ?? null,
    unrealizedROI: data?.wallet.unrealized_roi ?? null,
    unrealizedCost: data?.wallet.unrealized_cost ?? null,
    totalNftsHeld: data?.wallet.total_nfts_held ?? null,
    // Combined totals (realized + unrealized)
    get totalPnL() {
      if (this.realizedPnL === null && this.unrealizedPnL === null) return null;
      // Convert to numbers to avoid string concatenation (PostgreSQL NUMERIC serialization)
      const realized = this.realizedPnL !== null ? Number(this.realizedPnL) : 0;
      const unrealized = this.unrealizedPnL !== null ? Number(this.unrealizedPnL) : 0;
      return realized + unrealized;
    },
    // Count seller transactions for display (optional, for reference)
    transactionCount: data?.transactions.transactions.filter(
      tx => tx.from_address === id &&
            tx.from_address_profit_loss_xrp !== null &&
            tx.from_address_profit_loss_xrp !== undefined
    ).length || 0
  };

  // Calculate transaction type counts
  // Transaction counts come from server-side wallet metrics (computed by cron job)
  // This avoids the bug of only counting the first page of paginated transactions
  const transactionCounts = {
    buys: data?.wallet.total_buys ?? 0,
    sells: data?.wallet.total_sells ?? 0,
    transfers: data?.wallet.total_transfers ?? 0,
  };

  // Get average hold time from database (calculated by wallet metrics cron job)
  // Falls back to null if wallet metrics not yet available
  const avgHoldTimeSold = data?.wallet.avg_hold_time_sold_seconds ?? null;

  const handleTabChange = (newTab: TabType) => {
    setActiveTab(newTab);

    // If we're in a collection view, navigate back to base wallet URL
    if (collectionId) {
      // Navigate to base wallet URL with optional tab parameter
      const tabParam = newTab === 'history' ? '' : `?tab=${newTab}`;
      navigate(`/wallets/${id}${tabParam}`);
    } else {
      // Update URL with new tab (only if not in collection view)
      const params = new URLSearchParams(searchParams);
      if (newTab === 'history') {
        params.delete('tab'); // Default to History, so no param needed
      } else {
        params.set('tab', newTab);
      }
      setSearchParams(params);
    }
  };

  const handleCopyAddress = async () => {
    if (!data?.wallet.address) return;

    try {
      await navigator.clipboard.writeText(data.wallet.address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  // Handle follow/unfollow action with optimistic updates and rollback on error
  const handleFollow = async () => {
    if (!data?.wallet.address || !isAuthenticated) {
      // Show login modal if not authenticated
      openSignIn();
      return;
    }

    // Debounce: prevent rapid clicking (leading edge - execute first, block subsequent)
    const now = Date.now();
    if (now - lastFollowClickRef.current < FOLLOW_DEBOUNCE_MS) {
      return;
    }
    lastFollowClickRef.current = now;

    // Store previous state for rollback on error
    const prevIsFollowing = isFollowing;
    const prevFollowerCount = followerCount;

    setIsFollowLoading(true);

    // Optimistic update - update UI immediately
    if (isFollowing) {
      setIsFollowing(false);
      setFollowerCount(prev => Math.max(0, prev - 1));
    } else {
      setIsFollowing(true);
      setFollowerCount(prev => prev + 1);
    }

    try {
      if (prevIsFollowing) {
        await unfollowWallet(data.wallet.address);
        showSuccess('Wallet unfollowed', { title: 'Unfollowed' });
      } else {
        const response = await followWallet(data.wallet.address);
        if (response.alreadyFollowing) {
          // Was already following - revert the follower count increment
          setFollowerCount(prevFollowerCount);
        } else {
          showSuccess('You will be notified when this wallet buys or sells NFTs', { title: 'Following Wallet' });
        }
      }
    } catch (err: any) {
      // Rollback optimistic update on error
      setIsFollowing(prevIsFollowing);
      setFollowerCount(prevFollowerCount);

      // Extract error message from API response
      const errorMessage = err?.message || 'Failed to update follow status';
      showError(errorMessage, { title: 'Follow Error' });
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Handle block/unblock action with optimistic updates and rollback on error
  const handleBlock = async () => {
    if (!data?.wallet.address || !isAuthenticated) {
      openSignIn();
      return;
    }

    // Debounce: prevent rapid clicking
    const now = Date.now();
    if (now - lastBlockClickRef.current < BLOCK_DEBOUNCE_MS) {
      return;
    }
    lastBlockClickRef.current = now;

    const prevIsBlocked = isBlocked;
    setIsBlockLoading(true);

    // Optimistic update
    setIsBlocked(!isBlocked);

    try {
      if (prevIsBlocked) {
        await unblockWallet(data.wallet.address);
        showSuccess('Wallet unblocked', { title: 'Unblocked' });
      } else {
        await blockWallet(data.wallet.address);
        showSuccess('Offers from this wallet will be ignored', { title: 'Wallet Blocked' });
      }
    } catch (err: any) {
      // Rollback optimistic update on error
      setIsBlocked(prevIsBlocked);
      const errorMessage = err?.message || 'Failed to update block status';
      showError(errorMessage, { title: 'Block Error' });
    } finally {
      setIsBlockLoading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-12 bg-default-background py-12 overflow-auto mobile:gap-8 mobile:py-6 mobile:px-4">
        {/* Wallet Header */}
        <div className="flex w-full flex-col items-center gap-8">
          <div className="flex w-full flex-col items-center gap-6">
            <div className="flex items-center gap-2 flex-wrap justify-center mobile:flex-col mobile:gap-1">
              <span className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
                {data.wallet.displayName || 'Wallet'}
              </span>
              {data.wallet.isOwnWallet && (
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  You
                </span>
              )}
              {data.wallet.isAmbassador && <AmbassadorBadge size="md" />}
            </div>

            {/* Wallet Address */}
            <div className="flex items-center gap-2">
              <span className="text-lg text-subtext-color font-mono mobile:text-sm mobile:break-all mobile:text-center">
                {data.wallet.address}
              </span>
              <button
                onClick={handleCopyAddress}
                className="p-1.5 hover:bg-neutral-100 rounded transition-colors"
                title={copiedAddress ? 'Copied!' : 'Copy address'}
              >
                {copiedAddress ? (
                  <SubframeCore.FeatherCheck className="w-5 h-5 text-success-600" />
                ) : (
                  <SubframeCore.FeatherCopy className="w-5 h-5 text-subtext-color hover:text-default-font" />
                )}
              </button>
            </div>

            {/* Follower Count and Follow Button - Stacked vertically */}
            <div className="flex flex-col items-center gap-3 mt-2">
              {/* Follower Count */}
              <div className="flex items-center gap-2">
                <SubframeCore.FeatherUsers className="w-5 h-5 text-subtext-color" />
                <span className="text-lg text-subtext-color">
                  <span className="font-semibold text-default-font">{followerCount.toLocaleString()}</span>
                  {' '}{followerCount === 1 ? 'follower' : 'followers'}
                </span>
              </div>

              {/* Follow and Message Buttons - Only show if not own wallet */}
              {!data.wallet.isOwnWallet && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    variant={isFollowing ? 'neutral-secondary' : 'brand-primary'}
                    size="medium"
                    onClick={handleFollow}
                    disabled={isFollowLoading}
                    icon={isFollowing ? <SubframeCore.FeatherUserMinus className="w-5 h-5" /> : <SubframeCore.FeatherUserPlus className="w-5 h-5" />}
                  >
                    {isFollowLoading ? (
                      <span className="flex items-center gap-1.5">
                        <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                        {isFollowing ? 'Unfollowing...' : 'Following...'}
                      </span>
                    ) : (
                      isFollowing ? 'Unfollow' : 'Follow'
                    )}
                  </Button>
                  {isAuthenticated && (
                    <>
                      <Button
                        variant="neutral-secondary"
                        size="medium"
                        onClick={() => setShowTradeBuilder(true)}
                        icon={<SubframeCore.FeatherArrowLeftRight className="w-5 h-5" />}
                      >
                        Trade
                      </Button>
                      <Button
                        variant="neutral-secondary"
                        size="medium"
                        onClick={() => navigate(`/inbox?start=${data.wallet.address}`)}
                        icon={<SubframeCore.FeatherMessageCircle className="w-5 h-5" />}
                      >
                        Message
                      </Button>
                      <Button
                        variant={isBlocked ? 'destructive-secondary' : 'neutral-secondary'}
                        size="medium"
                        onClick={handleBlock}
                        disabled={isBlockLoading}
                        icon={<SubframeCore.FeatherBan className={`w-5 h-5 ${!isBlocked ? 'text-red-500' : ''}`} />}
                      >
                        {isBlockLoading ? (
                          <span className="flex items-center gap-1.5">
                            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                            {isBlocked ? 'Unblocking...' : <span className="text-red-500">Blocking...</span>}
                          </span>
                        ) : (
                          isBlocked ? 'Unblock' : <span className="text-red-500">Block</span>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Divider after follow section */}
            <div className="flex h-px w-full max-w-md flex-none bg-neutral-border mt-2" />

            {/* Trading Performance Section */}
            {isAuthenticated ? (
              <div className="flex w-full flex-col items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <SubframeCore.FeatherTrendingUp className="w-5 h-5 text-brand-600" />
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    NFT Trading Performance
                  </span>
                </div>

                {/* Total P&L - Headline Number */}
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    Total P&L
                  </span>
                  <div className="relative group">
                    <SubframeCore.FeatherHelpCircle className="w-3.5 h-3.5 text-neutral-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-1.5 py-0.5 bg-neutral-800 border border-solid border-neutral-900 rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg w-72">
                      <span className="text-[11px] leading-[1.35] font-caption text-black">
                        Combined profit/loss from both completed sales (realized) and currently held NFTs valued at collection floor prices (unrealized).
                      </span>
                    </div>
                  </div>
                </div>
                {pnlSummary.totalPnL !== null ? (
                  <span className={`text-heading-1 font-heading-1 mobile:text-heading-2 mobile:font-heading-2 ${
                    Number(pnlSummary.totalPnL) > 0
                      ? 'text-success-600'
                      : Number(pnlSummary.totalPnL) < 0
                        ? 'text-error-600'
                        : 'text-default-font'
                  }`}>
                    {Number(pnlSummary.totalPnL) > 0 ? '+' : Number(pnlSummary.totalPnL) < 0 ? '-' : ''}
                    {Math.abs(Number(pnlSummary.totalPnL)).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} XRP
                  </span>
                ) : (
                  <span className="text-heading-1 font-heading-1 mobile:text-heading-2 mobile:font-heading-2 text-subtext-color">
                    –
                  </span>
                )}
              </div>

              {/* P&L Breakdown Grid */}
              <div className="flex items-stretch justify-center gap-8 flex-wrap mt-2 mobile:gap-4 mobile:flex-col mobile:w-full">
                {/* Realized P&L */}
                <div className="flex flex-col items-center gap-1 px-6 py-3 bg-neutral-50 rounded-lg min-w-[140px] mobile:w-full mobile:px-4">
                  <div className="flex items-center gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Realized P&L
                    </span>
                    <div className="relative group">
                      <SubframeCore.FeatherHelpCircle className="w-3 h-3 text-neutral-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-1.5 py-0.5 bg-neutral-800 border border-solid border-neutral-900 rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg w-72">
                        <span className="text-[11px] leading-[1.35] font-caption text-black">
                          Actual profit/loss from NFTs you bought and then sold. This is your "locked in" gain or loss.
                        </span>
                      </div>
                    </div>
                  </div>
                  {pnlSummary.realizedPnL !== null ? (
                    <span className={`text-body-bold font-body-bold ${
                      Number(pnlSummary.realizedPnL) > 0
                        ? 'text-success-600'
                        : Number(pnlSummary.realizedPnL) < 0
                          ? 'text-error-600'
                          : 'text-default-font'
                    }`}>
                      {Number(pnlSummary.realizedPnL) > 0 ? '+' : Number(pnlSummary.realizedPnL) < 0 ? '-' : ''}
                      {Math.abs(Number(pnlSummary.realizedPnL)).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} XRP
                    </span>
                  ) : (
                    <span className="text-body-bold font-body-bold text-subtext-color">–</span>
                  )}
                  {pnlSummary.realizedROI !== null && (
                    <span className={`text-caption font-caption ${
                      Number(pnlSummary.realizedROI) > 0
                        ? 'text-success-500'
                        : Number(pnlSummary.realizedROI) < 0
                          ? 'text-error-500'
                          : 'text-subtext-color'
                    }`}>
                      {Number(pnlSummary.realizedROI) > 0 ? '+' : ''}{Number(pnlSummary.realizedROI).toFixed(1)}% ROI
                    </span>
                  )}
                </div>

                {/* Unrealized P&L */}
                <div className="flex flex-col items-center gap-1 px-6 py-3 bg-neutral-50 rounded-lg min-w-[140px] mobile:w-full mobile:px-4">
                  <div className="flex items-center gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Unrealized P&L
                    </span>
                    <div className="relative group">
                      <SubframeCore.FeatherHelpCircle className="w-3 h-3 text-neutral-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-1.5 py-0.5 bg-neutral-800 border border-solid border-neutral-900 rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg w-64">
                        <span className="text-[11px] leading-[1.35] font-caption text-black">
                          Estimated profit/loss from NFTs you still own, valued at current collection floor prices.
                        </span>
                      </div>
                    </div>
                  </div>
                  {pnlSummary.unrealizedPnL !== null ? (
                    <span className={`text-body-bold font-body-bold ${
                      Number(pnlSummary.unrealizedPnL) > 0
                        ? 'text-success-600'
                        : Number(pnlSummary.unrealizedPnL) < 0
                          ? 'text-error-600'
                          : 'text-default-font'
                    }`}>
                      {Number(pnlSummary.unrealizedPnL) > 0 ? '+' : Number(pnlSummary.unrealizedPnL) < 0 ? '-' : ''}
                      {Math.abs(Number(pnlSummary.unrealizedPnL)).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} XRP
                    </span>
                  ) : (
                    <span className="text-body-bold font-body-bold text-subtext-color">–</span>
                  )}
                  {pnlSummary.unrealizedROI !== null && (
                    <span className={`text-caption font-caption ${
                      Number(pnlSummary.unrealizedROI) > 0
                        ? 'text-success-500'
                        : Number(pnlSummary.unrealizedROI) < 0
                          ? 'text-error-500'
                          : 'text-subtext-color'
                    }`}>
                      {Number(pnlSummary.unrealizedROI) > 0 ? '+' : ''}{Number(pnlSummary.unrealizedROI).toFixed(1)}% ROI
                    </span>
                  )}
                </div>

                {/* Avg Hold Time */}
                <div className="flex flex-col items-center gap-1 px-6 py-3 bg-neutral-50 rounded-lg min-w-[140px] mobile:w-full mobile:px-4">
                  <div className="flex items-center gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Avg. Hold Time
                    </span>
                    <div className="relative group">
                      <SubframeCore.FeatherHelpCircle className="w-3 h-3 text-neutral-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-1.5 py-0.5 bg-neutral-800 border border-solid border-neutral-900 rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg w-52">
                        <span className="text-[11px] leading-[1.35] font-caption text-black">
                          Average time NFTs were held before being sold.
                        </span>
                      </div>
                    </div>
                  </div>
                  {avgHoldTimeSold !== null ? (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatHoldTime(avgHoldTimeSold)}
                    </span>
                  ) : (
                    <span className="text-body-bold font-body-bold text-subtext-color">–</span>
                  )}
                </div>
              </div>

              <p className="text-body font-body text-subtext-color text-center max-w-2xl text-xs mt-2">
                P&L excludes minted and transferred NFTs. Unrealized P&L is based on collection floor prices and updates hourly.
              </p>
              </div>
            ) : (
              /* Logged-out P&L Preview */
              <div className="flex w-full flex-col items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <SubframeCore.FeatherTrendingUp className="w-5 h-5 text-brand-600" />
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    NFT Trading Performance
                  </span>
                </div>

                {/* Preview showing what metrics are available */}
                <div className="flex flex-col items-center gap-6 py-6 px-8 bg-neutral-50 rounded-lg w-full max-w-2xl">
                  {/* Placeholder for Total P&L */}
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-caption font-caption text-subtext-color">Total P&L</span>
                    <div className="flex items-center gap-2">
                      <SubframeCore.FeatherLock className="w-5 h-5 text-neutral-400" />
                      <span className="text-heading-2 font-heading-2 text-neutral-400">+XXX.XX XRP</span>
                    </div>
                  </div>

                  {/* Preview boxes showing the breakdown categories */}
                  <div className="flex items-stretch justify-center gap-4 flex-wrap w-full">
                    <div className="flex flex-col items-center gap-1 px-4 py-3 bg-default-background rounded-md border border-neutral-border min-w-[120px] flex-1">
                      <span className="text-caption font-caption text-subtext-color">Realized P&L</span>
                      <span className="text-body font-body text-neutral-400">Completed trades</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 px-4 py-3 bg-default-background rounded-md border border-neutral-border min-w-[120px] flex-1">
                      <span className="text-caption font-caption text-subtext-color">Unrealized P&L</span>
                      <span className="text-body font-body text-neutral-400">Current holdings</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 px-4 py-3 bg-default-background rounded-md border border-neutral-border min-w-[120px] flex-1">
                      <span className="text-caption font-caption text-subtext-color">Avg. Hold Time</span>
                      <span className="text-body font-body text-neutral-400">Trade timing</span>
                    </div>
                  </div>

                  {/* Sign in CTA */}
                  <div className="flex flex-col items-center gap-3 mt-2">
                    <span className="text-body font-body text-default-font text-center">
                      Sign in to track your profit & loss across all NFT trades
                    </span>
                    <Button
                      variant="brand-primary"
                      onClick={openSignIn}
                    >
                      Sign In to View P&L
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Stats */}
            <div className="flex items-center justify-center gap-12 flex-wrap mt-6 mobile:gap-6">
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  Balance
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {data.wallet.xrpBalance != null
                    ? `${data.wallet.xrpBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} XRP`
                    : '—'}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  Collections
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {data.nfts.collections.length}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  NFTs Owned
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {data.nfts.total}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  Buys
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {transactionCounts.buys}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  Sells
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {transactionCounts.sells}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-caption font-caption text-subtext-color">
                  Transfers
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {transactionCounts.transfers}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Tabs and Content */}
        <div className="flex w-full flex-col items-start gap-6">
          <Tabs>
            <Tabs.Item active={activeTab === 'history'} onClick={() => handleTabChange('history')}>
              History
            </Tabs.Item>
            <Tabs.Item active={activeTab === 'nfts'} onClick={() => handleTabChange('nfts')}>
              NFTs
            </Tabs.Item>
            <Tabs.Item active={activeTab === 'offers'} onClick={() => handleTabChange('offers')}>
              Offers
            </Tabs.Item>
          </Tabs>

          {/* NFTs Tab */}
          {activeTab === 'nfts' && (
            <div className="flex w-full flex-col items-start gap-4">
              {!isCollectionView ? (
                <>
                  <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                    Collections
                  </span>
                  {data.nfts.collections.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg w-full">
                      <p className="text-gray-600">No NFTs found in this wallet</p>
                    </div>
                  ) : (
                    <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid mobile:grid-cols-2 mobile:gap-3">
                      {data.nfts.collections.map((collection) => {
                        const isUnknown = collection.collectionName === 'Unknown Collection';
                        const displayName = isUnknown ? collection.collectionId : collection.collectionName;

                        return (
                          <a
                            key={collection.collectionId}
                            href={`/wallets/${id}/${collection.collectionId}?tab=nfts`}
                            className="flex grow shrink-0 basis-0 flex-col items-start overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-sm hover:shadow-lg transition-shadow"
                          >
                            {collection.collectionImage ? (
                              <img
                                className="w-full flex-none aspect-square object-cover bg-gray-100"
                                src={collection.collectionImage}
                                alt={displayName}
                              />
                            ) : (
                              <div className="w-full flex-none aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                <div className="text-center p-4">
                                  <SubframeCore.FeatherPackage className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                  <p className="text-xs text-gray-500">Collection not indexed</p>
                                </div>
                              </div>
                            )}
                            <div className="flex w-full flex-col items-start gap-2 px-4 py-4 h-[88px]">
                              <span className="text-body-bold font-body-bold text-default-font line-clamp-2">
                                {displayName}
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                {collection.nftCount} {collection.nftCount === 1 ? 'item' : 'items'}
                              </span>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {data.nfts.hasMore && (
                    <div className="mt-4 text-center w-full">
                      <p className="text-gray-600">More NFTs available. Pagination coming soon.</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Collection View - Show NFTs from selected collection */}
                  {selectedCollection ? (
                    <>
                      <div className="flex items-center gap-2 mb-6">
                        <a
                          href={`/wallets/${id}?tab=nfts`}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                        >
                          <SubframeCore.FeatherChevronLeft className="w-4 h-4" />
                          All Collections
                        </a>
                        <span className="text-gray-400">/</span>
                        <span className="text-sm text-gray-600">
                          {selectedCollection.collectionName === 'Unknown Collection'
                            ? selectedCollection.collectionId
                            : selectedCollection.collectionName}
                        </span>
                      </div>

                      <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-200">
                        {selectedCollection.collectionSlug ? (
                          <a href={`/collections/${selectedCollection.collectionSlug}`}>
                            {selectedCollection.collectionImage ? (
                              <img
                                className="w-24 h-24 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity"
                                src={selectedCollection.collectionImage}
                                alt={selectedCollection.collectionName}
                              />
                            ) : (
                              <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-200 hover:opacity-80 transition-opacity">
                                <SubframeCore.FeatherPackage className="w-10 h-10 text-gray-400" />
                              </div>
                            )}
                          </a>
                        ) : selectedCollection.collectionImage ? (
                          <img
                            className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                            src={selectedCollection.collectionImage}
                            alt={selectedCollection.collectionName}
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-200">
                            <SubframeCore.FeatherPackage className="w-10 h-10 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h2 className="text-heading-2 font-heading-2 text-default-font mb-2">
                            {selectedCollection.collectionSlug ? (
                              <a href={`/collections/${selectedCollection.collectionSlug}`} className="hover:text-blue-400 transition-colors">
                                {selectedCollection.collectionName === 'Unknown Collection'
                                  ? selectedCollection.collectionId
                                  : selectedCollection.collectionName}
                              </a>
                            ) : (
                              selectedCollection.collectionName === 'Unknown Collection'
                                ? selectedCollection.collectionId
                                : selectedCollection.collectionName
                            )}
                          </h2>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-body font-body text-subtext-color">
                              {selectedCollection.nftCount} {selectedCollection.nftCount === 1 ? 'item' : 'items'} owned
                              {(() => {
                                const totalPoints = selectedCollection.nfts.reduce((sum, nft) => {
                                  const pts = nft.collection_points ? Number(nft.collection_points) : 0;
                                  return sum + pts;
                                }, 0);
                                return totalPoints > 0 ? (
                                  <> &middot; {formatPoints(totalPoints)} pts</>
                                ) : null;
                              })()}
                            </span>
                            {selectedCollection.collectionName === 'Unknown Collection' && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                Not indexed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Flip Opportunity Alert */}
                      {flipOpportunity && flipOpportunity.suggested_offer_low && flipOpportunity.suggested_offer_high && (
                        <div className="w-full p-4 rounded-lg border border-success-200 bg-success-50 mb-6">
                          <div className="flex items-start gap-3">
                            <SubframeCore.FeatherRefreshCw className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-success-700">
                                Potential Flip Opportunity
                              </span>
                              <span className="text-body font-body text-success-700">
                                This wallet recently sold{' '}
                                <a
                                  href={`/nfts/${flipOpportunity.nft_id}`}
                                  className="underline hover:text-success-800"
                                >
                                  {flipOpportunity.nft_name ? `"${flipOpportunity.nft_name}"` : 'an NFT'}
                                </a>
                                {' '}in this collection for{' '}
                                <strong>{flipOpportunity.discount_percentage.toFixed(0)}% below floor</strong> ({flipOpportunity.sale_price.toLocaleString()} XRP when floor was {flipOpportunity.floor_price_at_sale.toLocaleString()} XRP).
                                They may be motivated to sell their remaining NFTs at a discount.
                              </span>
                              <span className="text-body-bold font-body-bold text-success-700 mt-2">
                                Suggested offer: {flipOpportunity.suggested_offer_low === flipOpportunity.suggested_offer_high
                                  ? `${flipOpportunity.suggested_offer_low.toLocaleString()} XRP`
                                  : `${flipOpportunity.suggested_offer_low.toLocaleString()} – ${flipOpportunity.suggested_offer_high.toLocaleString()} XRP`}
                              </span>
                              {flipOpportunity.current_floor_price && (
                                <span className="text-caption font-caption text-success-600">
                                  Current floor: {flipOpportunity.current_floor_price.toLocaleString()} XRP
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Wallet offer controls banner */}
                      {walletOfferControls && walletOfferControls.wallet_offers_disabled && (
                        <div className="w-full flex items-center gap-2 mb-2">
                          <span className="text-body font-body text-subtext-color">
                            Note: This wallet is not accepting offers on their NFTs
                          </span>
                          {isOwnWallet && (
                            <a href="/settings" className="text-body font-body text-brand-primary hover:underline ml-auto">
                              Change in Settings
                            </a>
                          )}
                        </div>
                      )}
                      {walletOfferControls && !walletOfferControls.wallet_offers_disabled && walletOfferControls.wallet_offer_control_mode === 'floor_relative' && (
                        <div className="w-full flex items-center gap-2 mb-2">
                          <span className="text-body font-body text-subtext-color">
                            Note: This wallet has offer controls in place, all buy offers must be at least {Math.abs(walletOfferControls.wallet_offer_floor_percentage)}%{' '}
                            {walletOfferControls.wallet_offer_floor_percentage >= 0 ? 'above' : 'below'} collection floor price
                          </span>
                          {isOwnWallet && (
                            <a href="/settings" className="text-body font-body text-brand-primary hover:underline ml-auto">
                              Change in Settings
                            </a>
                          )}
                        </div>
                      )}

                      {isIndexedCollection ? (
                        <>
                          <NFTFilterBar
                            filters={filters}
                            traitStats={getCollectionTraitStats()}
                            hasVariablePoints={collectionMeta?.has_points_system}
                            isAuthenticated={isAuthenticated}
                            hideOwnershipFilter={true}
                          />
                          {collectionNFTsLoading && collectionNFTs.length === 0 ? (
                            <div className="flex items-center justify-center py-12 w-full">
                              <LoadingSpinner />
                            </div>
                          ) : collectionNFTs.length === 0 ? (
                            <div className="text-center py-12 rounded-lg w-full border border-solid border-neutral-border bg-default-background">
                              <p className="text-subtext-color">No NFTs match the current filters</p>
                            </div>
                          ) : (
                            <>
                              <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid mobile:grid-cols-2 mobile:gap-3">
                                {collectionNFTs.map((nft: any) => (
                                  <NFTCard
                                    key={nft.nft_token_id || nft.nftTokenId}
                                    nft={nft}
                                    showBuyButton={false}
                                    totalSupply={selectedCollection.totalSupply ?? undefined}
                                    useOriginalMedia={collectionMeta?.use_original_media}
                                  />
                                ))}
                              </div>
                              {collectionNFTsLoading && (
                                <div className="flex items-center justify-center py-4 w-full">
                                  <LoadingSpinner />
                                </div>
                              )}
                              <div ref={collectionSentinelRef} className="h-4 w-full" />
                            </>
                          )}
                        </>
                      ) : (
                        <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid mobile:grid-cols-2 mobile:gap-3">
                          {selectedCollection.nfts.map((nft) => (
                            <NFTCard
                              key={nft.nftTokenId}
                              nft={nft}
                              showBuyButton={false}
                              totalSupply={selectedCollection.totalSupply ?? undefined}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 rounded-lg w-full border border-solid border-neutral-border bg-default-background">
                      <p className="text-subtext-color">Collection not found</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Offers Tab */}
          {activeTab === 'offers' && (
            <div className="flex w-full flex-col items-start gap-8">
              {/* Incoming Transfers (grouped by collection) */}
              <IncomingTransferGroups
                offers={data.offers.incomingTransfers}
                isOwnWallet={data.wallet.isOwnWallet}
                processing={processing}
                currentOfferId={currentOfferId}
                onAcceptTransfer={acceptTransfer}
                onRejectTransfer={rejectTransfer}
                onBlockAddress={handleBlockFromOffer}
                blockingAddress={blockingAddress}
                onOfferRemoved={(offerId) => handleOfferRemoved(offerId, 'acceptTransfer')}
                onCacheInvalidate={async () => {
                  // Invalidate backend Redis cache first, then frontend React Query cache
                  if (id) {
                    try {
                      await apiClient.post(`/wallets/${id}/invalidate-cache`, {});
                    } catch {
                      // Non-critical — Redis TTL will expire naturally
                    }
                  }
                  queryClient.invalidateQueries({ queryKey: ['wallet', id || ''] });
                }}
              />

              {/* Outgoing Transfers */}
              {data.offers.outgoingTransfers.length > 0 && (
                <div className="flex w-full flex-col items-start gap-4">
                  <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                    Outgoing Transfers
                  </span>
                  <div className="w-full overflow-x-auto">
                  <Table
                    header={
                      <Table.HeaderRow>
                        <Table.HeaderCell>NFT</Table.HeaderCell>
                        <Table.HeaderCell>To</Table.HeaderCell>
                        {data.wallet.isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
                      </Table.HeaderRow>
                    }
                  >
                    {data.offers.outgoingTransfers.map((offer) => (
                      <Table.Row key={offer.offerId} className="h-20">
                        <Table.Cell className="h-20">
                          <a
                            href={`/nfts/${offer.nftTokenId}`}
                            className="flex items-center gap-3 hover:opacity-80"
                          >
                            <Avatar
                              size="large"
                              image={offer.nftImage || undefined}
                              square={true}
                            >
                              N
                            </Avatar>
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {offer.nftName || 'Unnamed NFT'}
                            </span>
                          </a>
                        </Table.Cell>
                        <Table.Cell className="h-20">
                          {renderLinkedAddress(offer.destination)}
                        </Table.Cell>
                        {data.wallet.isOwnWallet && (
                          <Table.Cell className="h-20">
                            <Button
                              variant="neutral-secondary"
                              size="small"
                              onClick={() => cancelOffer(offer.offerId, offer.nftTokenId)}
                              disabled={processing && currentOfferId === offer.offerId}
                            >
                              Cancel
                            </Button>
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table>
                  </div>
                </div>
              )}

              {/* Offers Received */}
              {data.offers.offersReceived.length > 0 && (() => {
                const fundedOffers = data.offers.offersReceived.filter(offer => offer.isFunded !== false);
                const unfundedOffers = data.offers.offersReceived.filter(offer => offer.isFunded === false);

                return (
                  <div className="flex w-full flex-col items-start gap-4">
                    <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                      Offers Received
                    </span>

                    {/* Funded Offers Table */}
                    {fundedOffers.length > 0 && (
                      <div className="w-full overflow-x-auto">
                        <Table
                          header={
                            <Table.HeaderRow>
                              <Table.HeaderCell>NFT</Table.HeaderCell>
                              <Table.HeaderCell>Offer Amount</Table.HeaderCell>
                              <Table.HeaderCell>From</Table.HeaderCell>
                              <Table.HeaderCell>Source</Table.HeaderCell>
                              <Table.HeaderCell>Expires</Table.HeaderCell>
                              {data.wallet.isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
                            </Table.HeaderRow>
                          }
                        >
                          {fundedOffers.map((offer) => {
                            const source = getOfferSource(offer.destination);
                            return (
                              <Table.Row key={offer.offerId} className="h-20">
                                <Table.Cell className="h-20">
                                  <a
                                    href={`/nfts/${offer.nftTokenId}`}
                                    className="flex items-center gap-3 hover:opacity-80"
                                  >
                                    <Avatar
                                      size="large"
                                      image={offer.nftImage || undefined}
                                      square={true}
                                    >
                                      N
                                    </Avatar>
                                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                      {offer.nftName || 'Unnamed NFT'}
                                    </span>
                                  </a>
                                </Table.Cell>
                                <Table.Cell className="h-20">
                                  <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                    {offer.price.toFixed(2)} {offer.currency}
                                  </span>
                                </Table.Cell>
                                <Table.Cell className="h-20">
                                  {renderLinkedAddress(offer.owner)}
                                </Table.Cell>
                                <Table.Cell className="h-20">
                                  <Badge variant={source.variant} className={source.className}>{source.label}</Badge>
                                </Table.Cell>
                                <Table.Cell className="h-20">
                                  <span className="whitespace-nowrap text-body font-body text-default-font">
                                    {offer.expiration ? formatExpiration(new Date(offer.expiration * 1000).toISOString()) : 'Never'}
                                  </span>
                                </Table.Cell>
                                {data.wallet.isOwnWallet && (
                                  <Table.Cell className="h-20">
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="brand-primary"
                                        size="small"
                                        onClick={() => acceptBuyOffer(
                                          offer.offerId,
                                          offer.nftTokenId,
                                          offer.destination,
                                          Math.floor(offer.price * 1_000_000) // Convert XRP to drops
                                        )}
                                        disabled={processing && currentOfferId === offer.offerId}
                                      >
                                        Accept
                                      </Button>
                                      <Button
                                        variant="neutral-secondary"
                                        size="small"
                                        onClick={() => handleDismissOffer(offer.offerId)}
                                        disabled={dismissingOfferId === offer.offerId}
                                      >
                                        Reject
                                      </Button>
                                      <Button
                                        variant="destructive-secondary"
                                        size="small"
                                        onClick={() => handleBlockFromOffer(offer.owner)}
                                        disabled={blockingAddress === offer.owner}
                                      >
                                        Block
                                      </Button>
                                    </div>
                                  </Table.Cell>
                                )}
                              </Table.Row>
                            );
                          })}
                        </Table>
                      </div>
                    )}

                    {/* Unfunded Offers Collapsible Section */}
                    {unfundedOffers.length > 0 && (
                      <div className="w-full flex flex-col items-start gap-2">
                        <button
                          onClick={() => setShowUnfundedOffers(!showUnfundedOffers)}
                          className="flex items-center gap-2 text-body font-body text-subtext-color hover:text-default-font transition-colors cursor-pointer"
                        >
                          {showUnfundedOffers ? (
                            <SubframeCore.FeatherChevronDown className="w-4 h-4" />
                          ) : (
                            <SubframeCore.FeatherChevronRight className="w-4 h-4" />
                          )}
                          <span>
                            {unfundedOffers.length} unfunded offer{unfundedOffers.length !== 1 ? 's' : ''} (buyer has insufficient funds)
                          </span>
                        </button>

                        {showUnfundedOffers && (
                          <div className="w-full overflow-x-auto">
                            <Table
                              header={
                                <Table.HeaderRow>
                                  <Table.HeaderCell>NFT</Table.HeaderCell>
                                  <Table.HeaderCell>Offer Amount</Table.HeaderCell>
                                  <Table.HeaderCell>From</Table.HeaderCell>
                                  <Table.HeaderCell>Source</Table.HeaderCell>
                                  <Table.HeaderCell>Expires</Table.HeaderCell>
                                  {data.wallet.isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
                                </Table.HeaderRow>
                              }
                            >
                              {unfundedOffers.map((offer) => {
                                const source = getOfferSource(offer.destination);
                                return (
                                  <Table.Row key={offer.offerId} className="h-20">
                                    <Table.Cell className="h-20">
                                      <a
                                        href={`/nfts/${offer.nftTokenId}`}
                                        className="flex items-center gap-3 hover:opacity-80"
                                      >
                                        <Avatar
                                          size="large"
                                          image={offer.nftImage || undefined}
                                          square={true}
                                        >
                                          N
                                        </Avatar>
                                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                          {offer.nftName || 'Unnamed NFT'}
                                        </span>
                                      </a>
                                    </Table.Cell>
                                    <Table.Cell className="h-20">
                                      <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                        {offer.price.toFixed(2)} {offer.currency}
                                      </span>
                                    </Table.Cell>
                                    <Table.Cell className="h-20">
                                      {renderLinkedAddress(offer.owner)}
                                    </Table.Cell>
                                    <Table.Cell className="h-20">
                                      <Badge variant={source.variant} className={source.className}>{source.label}</Badge>
                                    </Table.Cell>
                                    <Table.Cell className="h-20">
                                      <span className="whitespace-nowrap text-body font-body text-default-font">
                                        {offer.expiration ? formatExpiration(new Date(offer.expiration * 1000).toISOString()) : 'Never'}
                                      </span>
                                    </Table.Cell>
                                    {data.wallet.isOwnWallet && (
                                      <Table.Cell className="h-20">
                                        <div className="flex items-center gap-2">
                                          <div className="flex flex-col items-start gap-1">
                                            <Button
                                              variant="brand-primary"
                                              size="small"
                                              onClick={() => acceptBuyOffer(
                                                offer.offerId,
                                                offer.nftTokenId,
                                                offer.destination,
                                                Math.floor(offer.price * 1_000_000) // Convert XRP to drops
                                              )}
                                              disabled={true}
                                            >
                                              Accept
                                            </Button>
                                            <span className="text-xs text-error-600">
                                              Buyer has insufficient funds
                                            </span>
                                          </div>
                                          <Button
                                            variant="neutral-secondary"
                                            size="small"
                                            onClick={() => handleDismissOffer(offer.offerId)}
                                            disabled={dismissingOfferId === offer.offerId}
                                          >
                                            Reject
                                          </Button>
                                          <Button
                                            variant="destructive-secondary"
                                            size="small"
                                            onClick={() => handleBlockFromOffer(offer.owner)}
                                            disabled={blockingAddress === offer.owner}
                                          >
                                            Block
                                          </Button>
                                        </div>
                                      </Table.Cell>
                                    )}
                                  </Table.Row>
                                );
                              })}
                            </Table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Offers Made */}
              {data.offers.offersMade.length > 0 && (
                <div className="flex w-full flex-col items-start gap-4">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Offers Made
                  </span>
                  <Table
                    header={
                      <Table.HeaderRow>
                        <Table.HeaderCell>NFT</Table.HeaderCell>
                        <Table.HeaderCell>Offer Amount</Table.HeaderCell>
                        <Table.HeaderCell>Source</Table.HeaderCell>
                        <Table.HeaderCell>Expires</Table.HeaderCell>
                        {data.wallet.isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
                      </Table.HeaderRow>
                    }
                  >
                    {data.offers.offersMade.map((offer) => {
                      const source = getOfferSource(offer.destination);
                      return (
                        <Table.Row key={offer.offerId} className="h-20">
                          <Table.Cell className="h-20">
                            <a
                              href={`/nfts/${offer.nftTokenId}`}
                              className="flex items-center gap-3 hover:opacity-80"
                            >
                              <Avatar
                                size="large"
                                image={offer.nftImage || undefined}
                                square={true}
                              >
                                N
                              </Avatar>
                              <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                {offer.nftName || 'Unnamed NFT'}
                              </span>
                            </a>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {offer.price.toFixed(2)} {offer.currency}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <Badge variant={source.variant} className={source.className}>{source.label}</Badge>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <span className="whitespace-nowrap text-body font-body text-default-font">
                              {offer.expiration ? formatExpiration(new Date(offer.expiration * 1000).toISOString()) : 'Never'}
                            </span>
                          </Table.Cell>
                          {data.wallet.isOwnWallet && (
                            <Table.Cell className="h-20">
                              <Button
                                variant="neutral-secondary"
                                size="small"
                                onClick={() => cancelOffer(offer.offerId, offer.nftTokenId)}
                                disabled={processing && currentOfferId === offer.offerId}
                              >
                                Cancel
                              </Button>
                            </Table.Cell>
                          )}
                        </Table.Row>
                      );
                    })}
                  </Table>
                </div>
              )}

              {/* Listed Items */}
              {data.offers.listedNFTs.length > 0 && (
                <div className="flex w-full flex-col items-start gap-4">
                  <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                    Listed Items
                  </span>
                  <div className="w-full overflow-x-auto">
                  <Table
                    header={
                      <Table.HeaderRow>
                        <Table.HeaderCell>NFT</Table.HeaderCell>
                        <Table.HeaderCell>Price</Table.HeaderCell>
                        <Table.HeaderCell>Source</Table.HeaderCell>
                        <Table.HeaderCell>Expires</Table.HeaderCell>
                        {data.wallet.isOwnWallet && <Table.HeaderCell>Action</Table.HeaderCell>}
                      </Table.HeaderRow>
                    }
                  >
                    {data.offers.listedNFTs.map((offer) => {
                      const source = getOfferSource(offer.destination);
                      return (
                        <Table.Row key={offer.offerId} className="h-20">
                          <Table.Cell className="h-20">
                            <a
                              href={`/nfts/${offer.nftTokenId}`}
                              className="flex items-center gap-3 hover:opacity-80"
                            >
                              <Avatar
                                size="large"
                                image={offer.nftImage || undefined}
                                square={true}
                              >
                                N
                              </Avatar>
                              <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                                {offer.nftName || 'Unnamed NFT'}
                              </span>
                            </a>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {offer.price.toFixed(2)} {offer.currency}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <Badge variant={source.variant} className={source.className}>{source.label}</Badge>
                          </Table.Cell>
                          <Table.Cell className="h-20">
                            <span className="whitespace-nowrap text-body font-body text-default-font">
                              {offer.expiration ? formatExpiration(new Date(offer.expiration * 1000).toISOString()) : 'Never'}
                            </span>
                          </Table.Cell>
                          {data.wallet.isOwnWallet && (
                            <Table.Cell className="h-20">
                              <Button
                                variant="neutral-secondary"
                                size="small"
                                onClick={() => cancelOffer(offer.offerId, offer.nftTokenId)}
                                disabled={processing && currentOfferId === offer.offerId}
                              >
                                Cancel
                              </Button>
                            </Table.Cell>
                          )}
                        </Table.Row>
                      );
                    })}
                  </Table>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {totalOffers === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg w-full">
                  <p className="text-gray-600">No active offers for this wallet</p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                Transaction History
              </span>

              {/* Event Filter */}
              <div className="flex w-full items-center gap-2 flex-wrap">
                <span className="text-body font-body text-subtext-color">Event:</span>
                <div className="relative">
                  <Button
                    variant="neutral-secondary"
                    iconRight={<SubframeCore.FeatherChevronDown />}
                    onClick={() => setShowEventDropdown(!showEventDropdown)}
                  >
                    {eventFilter === 'all' ? 'All' : eventFilter === 'buys-sells' ? 'Buys and Sells' : 'Transfers'}
                  </Button>

                  {showEventDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-48 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-10">
                      {(['buys-sells', 'transfers', 'all'] as EventFilter[]).map((type) => (
                        <button
                          key={type}
                          onClick={() => {
                            setEventFilter(type);
                            setShowEventDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                            eventFilter === type ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                          }`}
                        >
                          {type === 'all' ? 'All' : type === 'buys-sells' ? 'Buys and Sells' : 'Transfers'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {(() => {
                const filteredTransactions = filterTransactions(data.transactions.transactions, eventFilter, id!);

                if (data.transactions.transactions.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-50 rounded-lg w-full">
                      <p className="text-gray-600">No transaction history found</p>
                    </div>
                  );
                }

                if (filteredTransactions.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-50 rounded-lg w-full">
                      <p className="text-gray-600">No {eventFilter === 'buys-sells' ? 'buy or sell' : 'transfer'} transactions found</p>
                    </div>
                  );
                }

                return (
                <div className="w-full overflow-x-auto">
                <Table
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Item</Table.HeaderCell>
                      <Table.HeaderCell>Event</Table.HeaderCell>
                      <Table.HeaderCell>Price</Table.HeaderCell>
                      <Table.HeaderCell>Seller Metrics</Table.HeaderCell>
                      <Table.HeaderCell>Time</Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {filteredTransactions.map((tx: any) => (
                    <Table.Row key={tx.id} className="h-20">
                      <Table.Cell className="h-20">
                        {tx.nft_name ? (
                          <a
                            href={`/nfts/${tx.nft_token_id}`}
                            className="flex items-center gap-3 hover:opacity-80"
                          >
                            <Avatar
                              size="large"
                              image={tx.nft_image || undefined}
                              square={true}
                            >
                              N
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-body-bold font-body-bold text-default-font">
                                {tx.nft_name}
                              </span>
                              {tx.collection_name && (
                                <span className="text-caption font-caption text-subtext-color">
                                  {tx.collection_name}
                                </span>
                              )}
                            </div>
                          </a>
                        ) : (
                          <div className="flex items-center gap-3">
                            <Avatar
                              size="large"
                              image={tx.nft_image || undefined}
                              square={true}
                            >
                              N
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-body-bold font-body-bold text-default-font">
                                Un-indexed NFT
                              </span>
                              {tx.collection_name && (
                                <span className="text-caption font-caption text-subtext-color">
                                  {tx.collection_name}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="h-20">
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(tx.type, tx.price, tx.from_address, tx.to_address, id!)}
                          <span className={`whitespace-nowrap text-body-bold font-body-bold ${getTransactionLabelColor(tx.type, tx.price, tx.from_address, tx.to_address, id!)}`}>
                            {getTransactionLabel(tx.type, tx.price, tx.from_address, tx.to_address, id!)}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="h-20">
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {tx.price !== null && tx.price !== undefined ? `${Number(tx.price).toFixed(2)} XRP` : '–'}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="h-20">
                        {(() => {
                          // Only show seller metrics if this wallet was the seller (from_address)
                          if (tx.from_address !== id) {
                            return (
                              <span className="whitespace-nowrap text-body font-body text-subtext-color">
                                –
                              </span>
                            );
                          }

                          const metrics = formatSellerMetrics(
                            tx.from_address_hold_duration_seconds,
                            tx.from_address_profit_loss_xrp,
                            tx.from_address_roi_percentage
                          );

                          if (!metrics) {
                            return (
                              <span className="whitespace-nowrap text-body font-body text-subtext-color">
                                –
                              </span>
                            );
                          }

                          return (
                            <span className="whitespace-nowrap text-body font-body">
                              <span className="text-default-font">{metrics.holdText}</span>
                              {' '}
                              <span className={metrics.plColor}>{metrics.plText}</span>
                            </span>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell className="h-20">
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {formatTimestamp(tx.timestamp)}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
                </div>
              );
            })()}
            </div>
          )}
        </div>
      </div>

      {/* Xaman Signing Modal */}
      {showXamanModal && xamanPayload && (
        <WalletSigningModal
          open={showXamanModal}
          onClose={handleTransactionRejected}
          title={getModalTitle()}
          payload={xamanPayload}
          onSuccess={handleTransactionSuccess}
          onRejected={handleTransactionRejected}
          onExpired={handleTransactionRejected}
        />
      )}

      {/* Trade Builder Modal */}
      {showTradeBuilder && data?.wallet?.address && (
        <TradeBuilder
          isOpen={showTradeBuilder}
          onClose={() => setShowTradeBuilder(false)}
          onTradeCreated={() => {
            setShowTradeBuilder(false);
            showSuccess('Trade proposal sent!');
          }}
          recipientAddress={data.wallet.address}
        />
      )}
    </DefaultPageLayout>
  );
}
